import type { AppRole, LoansAppRole } from '@/types';

export const LOANS_APP_ROLE_QUERY_KEY = 'app-role';
const LOANS_APP_ROLE_STORAGE_KEY = 'vestra_current_user_role';
const VALID_LOANS_APP_ROLES: LoansAppRole[] = ['loans_admin', 'loans_user'];
const VALID_APP_ROLES: AppRole[] = [
  'sr_admin',
  'sr_issuer_admin',
  'sr_issuer',
  'sr_investor',
  'compliance_admin',
  'compliance_user',
  'loans_admin',
  'loans_user',
  'academy_user',
  'academy_organisation_admin',
];

export function isLoansAppRole(value: string | null | undefined): value is LoansAppRole {
  return !!value && VALID_LOANS_APP_ROLES.includes(value as LoansAppRole);
}

export function isAppRole(value: string | null | undefined): value is AppRole {
  return !!value && VALID_APP_ROLES.includes(value as AppRole);
}

export function getStoredLoansAppRole(): LoansAppRole | null {
  const value = localStorage.getItem(LOANS_APP_ROLE_STORAGE_KEY);
  return isLoansAppRole(value) ? value : null;
}

export function setStoredLoansAppRole(role: LoansAppRole | null) {
  if (!role) {
    localStorage.removeItem(LOANS_APP_ROLE_STORAGE_KEY);
    return;
  }

  localStorage.setItem(LOANS_APP_ROLE_STORAGE_KEY, role);
}

export function syncLoansAppRoleFromLocation() {
  const urlRole = new URLSearchParams(window.location.search).get(LOANS_APP_ROLE_QUERY_KEY);
  if (isLoansAppRole(urlRole)) {
    setStoredLoansAppRole(urlRole);
  }
}

export function resolveLoansAppRole(
  user?: {
    appRole?: string | null;
    role?: string | null;
    tenantId?: string | null;
    availableTenants?: Array<{ id: string; appRole?: string | null }>;
  } | null,
): LoansAppRole {
  const activeTenantRole = user?.availableTenants?.find(
    tenant => tenant.id === user?.tenantId,
  )?.appRole;
  if (isLoansAppRole(activeTenantRole)) {
    return activeTenantRole;
  }

  if (user?.appRole && isLoansAppRole(user.appRole)) {
    return user.appRole;
  }

  const storedRole = getStoredLoansAppRole();
  if (storedRole) {
    return storedRole;
  }

  return user?.role === 'ADMIN' ? 'loans_admin' : 'loans_user';
}

export function resolveCurrentAppRole(
  user?: {
    appRole?: string | null;
  } | null,
): AppRole | null {
  return isAppRole(user?.appRole) ? user.appRole : null;
}
